import { removeFloatingPointPrecisionError } from '../../../../functions/remove-floating-point-precision-error';
import { unclampedLerp } from '../../../../functions/utilities/unclamped-lerp';

export class RGBAColour {
  private _alpha: number;
  private _blue: number;
  private _green: number;
  private _red: number;

  /**
   * parses  a hex string
   *
   * @param hexString a string in the format #rrggbb or #rrggbbaa
   */
  static fromHex(str: string): RGBAColour {
    if (str.length === 7 || str.length === 9) {
      str = str.slice(1);
    }
    const r = this._parseHexColour(str.slice(0, 2));
    const g = this._parseHexColour(str.slice(2, 4));
    const b = this._parseHexColour(str.slice(4, 6));
    const a = this._parseHexColour(str.slice(6, 8), 1);

    return new RGBAColour(r, g, b, a);
  }
  /**
   * A simple RGBA Lerp
   *
   * Will lerp, or mix in this case between a and b. The lerp will be based on the t-value.
   * A t value of 0 will result in a being returned. A t value of 1 will result in b being returned.
   * This is unclamped, so values larger than 1 and smaller than 0 will produce results.
   * The colour itself is clamped so some of the properties might hit a limit before others
   * @param a
   * @param b
   * @param t
   */
  static lerp(a: RGBAColour, b: RGBAColour, t: number): RGBAColour {
    return new RGBAColour(
      unclampedLerp(a.red, b.red, t),
      unclampedLerp(a.green, b.green, t),
      unclampedLerp(a.blue, b.blue, t),
      unclampedLerp(a.alpha, b.alpha, t)
    );
  }
  private static _parseHexColour(str: string, defaultValue: number = 0): number {
    let parsed = parseInt(str, 16);
    return isNaN(parsed) ? defaultValue : parsed / 255;
  }
  public get alpha(): number {
    return this._alpha;
  }

  public get blue(): number {
    return this._blue;
  }

  public get green(): number {
    return this._green;
  }

  public get red(): number {
    return this._red;
  }

  /**
   * Create a colour using the colour components from 0 to 1;
   */
  constructor(red: number, green: number, blue: number, alpha: number = 1) {
    this._red = this._capValue(red);
    this._green = this._capValue(green);
    this._blue = this._capValue(blue);
    this._alpha = this._capValue(alpha);
  }

  equals(other: RGBAColour): boolean {
    return (
      this._red === other.red &&
      this._green === other.green &&
      this._blue === other.blue &&
      this._alpha === other._alpha
    );
  }

  private _capValue(value: number): number {
    return removeFloatingPointPrecisionError(Math.max(0, Math.min(value, 1)));
  }
}
